import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { analyticsAPI, ncfAPI } from '../services/api.jsx';
import { useAuth } from '../contexts/AuthContext';
import {
  Users,
  FileText,
  Mail,
  CalendarDays,
  Calculator,
  PenTool,
  Monitor,
  ShieldCheck,
  Newspaper,
  Briefcase,
  Building2,
  User,
  ClipboardList,
  FilePlus,
  Receipt
} from 'lucide-react';
import './Dashboard.css';

// Permission-to-Action mapping
// Each permission area maps to the quick actions available in that area
const PERMISSION_TO_ACTIONS = {
  contacts: {
    id: 'manage-contacts',
    label: 'Manage Contacts',
    href: '/contacts',
    icon: Users,
    permission: 'contacts',
    description: 'View, add, and organize your contact lists',
    category: 'Contacts',
    group: 'primary',
    priority: 2,
  },
  templates: {
    id: 'create-template',
    label: 'Create Template',
    href: '/templates',
    icon: FileText,
    permission: 'templates',
    description: 'Design and save email templates with variables',
    category: 'Templates',
    group: 'primary',
    priority: 3,
  },
  campaigns: {
    id: 'new-campaign',
    label: 'New Campaign',
    href: '/campaigns',
    icon: Mail,
    permission: 'campaigns',
    description: 'Create and launch bulk email campaigns',
    category: 'Campaigns',
    group: 'primary',
    priority: 1,
  },
  events: {
    id: 'manage-events',
    label: 'Manage Events',
    href: '/events',
    icon: CalendarDays,
    permission: 'events',
    description: 'Create events and manage registrations',
    category: 'Events',
    group: 'primary',
    priority: 4,
  },
  ncf: {
    id: 'ncf-history',
    label: 'NCF History',
    href: '/ncfs',
    icon: ClipboardList,
    permission: 'ncf',
    description: 'Track NCF submissions and onboarding status',
    category: 'NCF',
    group: 'primary',
    priority: 5,
  },
  'ncf-write': {
    id: 'ncf-add',
    label: 'Add an NCF',
    href: '/ncfs?new=1',
    icon: FilePlus,
    permission: 'ncf-write',
    description: 'Start a new consultant form',
    category: 'NCF',
    group: 'primary',
    priority: 6,
  },
  'rate-calculator': {
    id: 'rate-calculator',
    label: 'Rate Calculator',
    href: '/rate-calculator',
    icon: Calculator,
    permission: 'rate-calculator',
    description: 'Calculate C2C, W2, and FT contractor rates',
    category: 'Tools',
    group: 'tools',
    priority: 1,
  },
  'signature-builder': {
    id: 'signature-builder',
    label: 'Email Signature Builder',
    href: '/signature-builder',
    icon: PenTool,
    permission: 'signature-builder',
    description: 'Build and copy Outlook-ready email signatures',
    category: 'Tools',
    group: 'tools',
    priority: 2,
  },
  'video-backgrounds': {
    id: 'video-backgrounds',
    label: 'Meeting Backgrounds',
    href: '/video-backgrounds',
    icon: Monitor,
    permission: 'video-backgrounds',
    description: 'Generate and manage branded video call backgrounds',
    category: 'Tools',
    group: 'tools',
    priority: 3,
  },
  billing: {
    id: 'billing',
    label: 'Billing',
    href: '/billing',
    icon: Receipt,
    permission: 'billing',
    description: 'Find, download, and email AR invoices from SharePoint',
    category: 'Tools',
    group: 'tools',
    priority: 4,
  },
  admin: {
    id: 'admin',
    label: 'Admin Panel',
    href: '/admin',
    icon: ShieldCheck,
    permission: 'admin',
    description: 'Manage users, roles, and system settings',
    category: 'Admin',
    group: 'admin',
    priority: 1,
  },
  news: {
    id: 'manage-news',
    label: 'Manage News',
    href: '/news',
    icon: Newspaper,
    permission: 'news',
    description: 'Create and publish news articles',
    category: 'Website',
    group: 'website',
    priority: 1,
  },
  careers: {
    id: 'manage-careers',
    label: 'Manage Careers',
    href: '/careers',
    icon: Briefcase,
    permission: 'careers',
    description: 'Post and manage career opportunities',
    category: 'Website',
    group: 'website',
    priority: 2,
  },
  'team-members': {
    id: 'manage-team',
    label: 'Manage Team',
    href: '/team-members',
    icon: User,
    permission: 'team-members',
    description: 'Manage team member profiles and information',
    category: 'Website',
    group: 'website',
    priority: 3,
  },
  'manage-clients': {
    id: 'manage-clients',
    label: 'Manage Clients',
    href: '/rate-calculator/clients',
    icon: Building2,
    permission: 'manage-clients',
    description: 'View and manage client data for rate calculations',
    category: 'Admin',
    group: 'admin',
    priority: 2,
  },
};

// Pure date strings (e.g. DesiredStartDate) are UTC calendar dates; parse as local to avoid a
// one-day-back shift when the browser is behind UTC.
const formatStartDate = (value) => {
  if (!value) return '-';
  const dateOnlyMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (dateOnlyMatch) {
    const [, year, month, day] = dateOnlyMatch.map(Number);
    return new Date(year, month - 1, day).toLocaleDateString();
  }
  return new Date(value).toLocaleDateString();
};

/**
 * Build quick actions based on user's actual permissions
 * @param {Array} permissions - Array of permission strings (e.g., ['contacts', 'campaigns', 'templates'])
 * @returns {Array} Actions that user has permission for
 */
const buildActionsFromPermissions = (permissions) => {
  if (!permissions || !Array.isArray(permissions)) return [];
  
  const actions = [];
  const actionIds = new Set();
  
  // For each permission user has, add the corresponding action
  permissions.forEach(permission => {
    const action = PERMISSION_TO_ACTIONS[permission];
    if (action && !actionIds.has(action.id)) {
      actions.push(action);
      actionIds.add(action.id);
    }
  });
  
  return actions;
};

/**
 * Sort actions by category
 * @param {Array} actions - Array of action objects
 * @returns {Array} Actions sorted by category
 */
const sortActionsByCategory = (actions) => {
  const categoryOrder = ['Contacts', 'Templates', 'Campaigns', 'Events', 'NCF', 'Tools', 'Website', 'Admin', 'Other'];
  
  return [...actions].sort((a, b) => {
    const catA = categoryOrder.indexOf(a.category || 'Other');
    const catB = categoryOrder.indexOf(b.category || 'Other');
    
    if (catA !== catB) {
      return catA - catB;
    }
    
    return a.label.localeCompare(b.label);
  });
};

const ACTION_SECTIONS = [
  {
    key: 'primary',
    title: 'Primary Actions',
    subtitle: 'Most common tasks to start and manage campaigns',
  },
  {
    key: 'tools',
    title: 'Tools',
    subtitle: 'Utilities that support your day-to-day work',
  },
  {
    key: 'website',
    title: 'Website Content',
    subtitle: 'Manage public-facing website content',
  },
  {
    key: 'admin',
    title: 'Admin Tools',
    subtitle: 'Configuration, permissions, and operational controls',
  },
];

const Dashboard = () => {
  const location = useLocation();
  const { effectiveRole, viewAsUser, isViewingAsUser, canViewAsUsers, hasAnyPermission } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [permissionMessage, setPermissionMessage] = useState(null);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [pendingStartDates, setPendingStartDates] = useState([]);

  const permissionFlags = useMemo(() => ({
    contacts: hasAnyPermission(['contacts:read:own', 'contacts:read:any']),
    templates: hasAnyPermission(['templates:read:own', 'templates:read:any']),
    campaigns: hasAnyPermission(['campaigns:read:own', 'campaigns:read:any']),
    events: hasAnyPermission(['events:read:own', 'events:read:any']),
    ncf: hasAnyPermission(['ncf:read:own', 'ncf:read:any']),
    'ncf-write': hasAnyPermission(['ncf:write:own', 'ncf:write:any']),
    'rate-calculator': hasAnyPermission(['rate-calculator:use']),
    'signature-builder': hasAnyPermission(['signature-builder:use']),
    'video-backgrounds': hasAnyPermission(['video-backgrounds:use']),
    billing: hasAnyPermission(['billing:read']),
    news: hasAnyPermission(['news:read']),
    careers: hasAnyPermission(['careers:read']),
    'team-members': hasAnyPermission(['team-members:read']),
    admin: hasAnyPermission(['admin:users:read', 'admin:users:write', 'admin:permissions:write', 'admin:ops:emailworker']),
    'manage-clients': hasAnyPermission(['rate-clients:read', 'rate-clients:write']),
  }), [hasAnyPermission]);

  const enabledPermissions = useMemo(
    () => Object.entries(permissionFlags)
      .filter(([, allowed]) => Boolean(allowed))
      .map(([permissionKey]) => permissionKey),
    [permissionFlags]
  );

  const availableActions = useMemo(() => {
    return sortActionsByCategory(buildActionsFromPermissions(enabledPermissions)).sort((a, b) => {
      const priorityA = Number.isFinite(a.priority) ? a.priority : 999;
      const priorityB = Number.isFinite(b.priority) ? b.priority : 999;
      if (priorityA !== priorityB) return priorityA - priorityB;
      return a.label.localeCompare(b.label);
    });
  }, [enabledPermissions]);

  const groupedActions = useMemo(() => {
    const groups = {
      primary: [],
      tools: [],
      website: [],
      admin: [],
      other: [],
    };

    availableActions.forEach((action) => {
      const groupKey = action.group || 'other';
      if (!groups[groupKey]) {
        groups.other.push(action);
        return;
      }
      groups[groupKey].push(action);
    });

    return groups;
  }, [availableActions]);

  useEffect(() => {
    // Check for permission denial message from navigation
    if (location.state?.message) {
      setPermissionMessage(location.state.message);
      // Clear the message after 5 seconds
      const timer = setTimeout(() => setPermissionMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [location.state]);

  useEffect(() => {
    loadDashboard();
  }, []);

  useEffect(() => {
    ncfAPI.myPendingApprovals()
      .then(response => setPendingApprovals(response.data.approvals || []))
      .catch(() => setPendingApprovals([]));
    ncfAPI.myPendingStartDates()
      .then(response => setPendingStartDates(response.data.confirmations || []))
      .catch(() => setPendingStartDates([]));
  }, []);

  const loadDashboard = async () => {
    try {
      setLoading(true);
      const response = await analyticsAPI.getDashboard();
      setStats(response.data);
    } catch (err) {
      setError('Failed to load dashboard data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="loading">Loading dashboard...</div>;
  if (error) return <div className="error">{error}</div>;

  return (
    <div className="container">
      <h1>Dashboard</h1>

      {canViewAsUsers && (
        <div className={`view-as-badge ${isViewingAsUser ? 'active' : ''}`}>
          {isViewingAsUser
            ? `Viewing as ${viewAsUser?.displayName || viewAsUser?.email} • Access level: ${effectiveRole}`
            : `Current access level: ${effectiveRole}`}
        </div>
      )}
      
      {permissionMessage && (
        <div style={{
          padding: '12px 16px',
          backgroundColor: '#ffebee',
          border: '1px solid #ef5350',
          borderRadius: '4px',
          color: '#c62828',
          marginBottom: '20px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <span>❌ {permissionMessage}</span>
          <button
            onClick={() => setPermissionMessage(null)}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '18px',
              cursor: 'pointer',
              color: '#c62828'
            }}
          >
            ✕
          </button>
        </div>
      )}

      {pendingApprovals.length > 0 && (
        <div className="card ncf-pending-approvals-card">
          <h3>NCF reviews awaiting your approval</h3>
          <ul className="ncf-pending-approvals-list">
            {pendingApprovals.map(approval => (
              <li key={approval.approvalId}>
                <a href={`/ncfs/${approval.ncfId}`}>
                  {approval.candidateName}{approval.clientName ? ` • ${approval.clientName}` : ''}
                </a>
                <span className="ncf-pending-approval-role">{approval.role === 'salesperson' ? 'Salesperson' : approval.role === 'recruiter' ? 'Recruiter' : 'Reviewer'} approval</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {pendingStartDates.length > 0 && (
        <div className="card ncf-pending-approvals-card ncf-pending-start-dates-card">
          <h3>NCF start dates awaiting confirmation</h3>
          <ul className="ncf-pending-approvals-list">
            {pendingStartDates.map(confirmation => (
              <li key={confirmation.ncfId}>
                <a href={`/ncfs/${confirmation.ncfId}`}>
                  {confirmation.candidateName}{confirmation.clientName ? ` • ${confirmation.clientName}` : ''}
                </a>
                <span className="ncf-pending-approval-role">Started {formatStartDate(confirmation.desiredStartDate)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="stats-grid">
        {/* Marketing-specific stats moved to MarketingDashboard */}
        {/* Keep only non-marketing quick reference if needed */}
      </div>

      {enabledPermissions.length > 0 && (
        <div className="card">
          
          <div className="dashboard-actions-wrapper">
            {ACTION_SECTIONS.map((section) => {
              const sectionActions = groupedActions[section.key] || [];
              if (sectionActions.length === 0) return null;

              return (
                <section key={section.key} className="dashboard-action-section">
                  <div className="dashboard-action-section-header">
                    <h3 className="dashboard-action-section-title">{section.title}</h3>
                    <p className="dashboard-action-section-subtitle">{section.subtitle}</p>
                  </div>

                  <div className={`action-buttons ${section.key === 'primary' ? 'action-buttons-primary' : ''}`}>
                    {sectionActions.map((action) => {
                      const IconComponent = action.icon;
                      return (
                        <a
                          key={action.id}
                          href={action.href}
                          className="action-card"
                          title={action.description}
                        >
                          <div className="action-icon"><IconComponent size={42} strokeWidth={2} /></div>
                          <div className="action-label">{action.label}</div>
                          <div className="action-description">{action.description}</div>
                        </a>
                      );
                    })}
                  </div>
                </section>
              );
            })}
          </div>
            
            {/* Clear Queue button - available to users with campaigns or contacts permission */}
            {(permissionFlags.campaigns || permissionFlags.contacts) && (
              <div style={{ marginTop: '2rem', paddingTop: '2rem', borderTop: '1px solid #eee' }}>
                <p style={{ color: '#666', marginBottom: '1rem', fontSize: '0.9rem' }}>
                  📊 For detailed marketing analytics and queue management, visit <a href="/marketing-dashboard" style={{ color: '#0066cc', textDecoration: 'none' }}>Marketing Dashboard</a>
                </p>
              </div>
            )}
        </div>
      )}


    </div>
  );
};

export default Dashboard;
